/**
 * This function removes all  html tags from the input text with the exception of
 * <sup>, <sub>, and any additional tags passed in through ignoreTags.
 * @param text string with author input text
 * @param ignoreTags tags to not be removed, should not include angle brackets or slash
 * @return string with specified tags removed. Any text within the tag will still be present
 */
import { TPrequalResponse } from '../types/responses/PrequalResponse';
import TestUtils from 'react-dom/test-utils';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { TCreditCard } from '../types/models/CreditCard';

export const sanitizeAuthorInput = (text: string = '', ignoreTags: string[] = []): string => {
    const ignore = ['(sup)', '(sub)'];
    ignoreTags.forEach(tag => {
        ignore.push(`(${tag})`);
    });
    // eslint-disable-next-line
    const regex = new RegExp('<\/\?\\b(?!' + ignore.join('|') + ').+?\/?>', 'g');
    return text ? text.replace(regex, '') : '';
};

export const getHostFromStringUrl = (url: string): string => {
    try {
        return new URL(url).host;
    } catch {
        return '';
    }
};

export const setCookie = (key: string, value: string | null, daysUntilExpired: number): void => {
    const expireDate = new Date(Date.now());
    expireDate.setDate(expireDate.getDate() + daysUntilExpired);
    document.cookie = key + '=' + value + '; expires=' + expireDate.toUTCString() + ';';
};

export const getCookie = (name: string): unknown => {
    const re = new RegExp(name + '=([^;]+)');
    const value = re.exec(document.cookie);
    return value !== null ? unescape(value[1]) : null;
};

export const cleanInternalLinkUrl = (url: string): string => {
    const isLocalHost = window.location.href.includes('4502');
    if (isLocalHost) {
        return url;
    }

    const patternsToRemove = [/\/content\/creditonebank\/en/g, /\/content\/cob-corp-acquisition\/us\/en/g, /\.html/g];
    const cleanUrl = patternsToRemove.reduce((acc, pattern) => acc.replace(pattern, ''), url);

    return cleanUrl.startsWith('?') ? `/${cleanUrl}` : cleanUrl;
};

/**
 *
 * @param className string of class that you would like to target for computed styles for testing
 * @returns Returns an object of all of the computed styles for the given element targeted.
 */
export const computedStyles = (className: string): CSSStyleDeclaration => {
    const section = document.querySelectorAll(className);
    return window.getComputedStyle(section[0]);
};

/*eslint-disable*/
/**
 * Converts a hex value to three RGB values as a string
 * @param {string} hex The hex value of a color as a string
 * @returns A string of three rgb values without the `rgb()` function
 */
export const hexToRgb = (hex: string): string => {
    const hexes = hex
        .replace(
            /^#?([a-f\d])([a-f\d])([a-f\d])$/ig,
            (r: string, g: string, b: string) => '#' + r + r + g + g + b + b,
        )
        .substring(1)
        .match(/.{2}/g);
    return hexes?.map((x: string) => parseInt(x, 16))
        .join(', ')
        .replace('rgb(', '')
        .replace(')', '');
};
/*eslint-enable*/

/**
     * Converts rgb values to a hex as a string
     * @param {number} r number value for the red color channel
     * @param {number} g number value for the green color channel
     * @param {number} b number value for the blue color channel
     * @returns A hex value as a string
     */
export const rgbToHex = (r: number, g: number, b: number): string => {
    return '#' + [r, g, b].map((x) => {
        x.toString(16).padStart(2, '0');
    }).join('');
};

/**
 * Converts rgb function to a hex as a string
 * @param rgbFunction string in the format of the rgb function ie: `rgb(24,64,150)`
 * @returns Hex value in a string format ie: `#44ff66`
 */
export const rgbFuncToHex = (rgbFunction: string): string => {
    const splitRGB = rgbFunction.split('(')[1].split(')')[0].split(', ');
    return rgbToHex(parseInt(splitRGB[0]), parseInt(splitRGB[1]), parseInt(splitRGB[2]));
};

export const getParamFromUrl = (paramName: string): string => {
    const params = (new URL(document.location.toString())).searchParams;
    return params.get(paramName) ? params.get(paramName) : null;
};

const cypherAlg = (iv: ArrayBuffer): {name: 'AES-GCM', iv: ArrayBuffer} => {
    return {
        name: 'AES-GCM',
        iv: iv,
    };
};

export const generateKey = async (password: string, iv: ArrayBuffer): Promise<CryptoKey> => {
    const pwHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
    return await window.crypto.subtle.importKey(
        'raw',
        pwHash,
        cypherAlg(iv),
        true,
        ['encrypt', 'decrypt'],
    ); 
};

export const generateIV = (): Uint8Array => {
    return window.crypto.getRandomValues(new Uint8Array(12));
};

export const encrypt = async (msg: string, key: CryptoKey, iv: ArrayBuffer): Promise<BufferSource> => {
    const encodedMsg = new TextEncoder().encode(msg);
    const cipher = await window.crypto.subtle.encrypt(
        cypherAlg(iv),
        key,
        encodedMsg,
    );

    return cipher as BufferSource;
};

export const decrypt = async (msg:ArrayBuffer, key: CryptoKey, iv: ArrayBuffer): Promise<string> => {
    const decrypted = await window.crypto.subtle.decrypt(
        cypherAlg(iv),
        key,
        msg,
    );

    const dec = new TextDecoder();
    return dec.decode(decrypted);
};

export const getBaseUrl = (): string => {
    return window.location.origin;
};

export const getParameterByName = (name: string): string => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
};

export const hexToBase64 = (hexString: string): string => {
    const parsed = hexString.match(/\w{2}/g)?.map((s) => String.fromCharCode(parseInt(s, 16))).join('');
    if (!parsed) {
        throw new Error('Error: hexToBase64(): converting argument to base 64');
    }

    return btoa(parsed);
};

export const getPaddingFromProps = (props: Record<string, any>, displayWidth: number, defaultZero?: boolean): Record<string, string> => {
    const isMobile = displayWidth <= 768;

    if (defaultZero) {
        return {
            paddingTop: (isMobile ? props.paddingMobileTop : props.paddingDesktopTop) ?? '0px',
            paddingLeft: (isMobile ? props.paddingMobileLeft : props.paddingDesktopLeft) ?? '0px',
            paddingRight: (isMobile ? props.paddingMobileRight : props.paddingDesktopRight) ?? '0px',
            paddingBottom: (isMobile ? props.paddingMobileBottom : props.paddingDesktopBottom) ?? '0px',
        };
    }

    return {
        paddingTop: isMobile ? props.paddingMobileTop : props.paddingDesktopTop,
        paddingLeft: isMobile ? props.paddingMobileLeft : props.paddingDesktopLeft,
        paddingRight: isMobile ? props.paddingMobileRight : props.paddingDesktopRight,
        paddingBottom: isMobile ? props.paddingMobileBottom : props.paddingDesktopBottom,
    };
};

export const getMarginFromProps = (props: Record<string, any>, displayWidth: number, defaultZero?: boolean): Record<string, string> => {
    const isMobile = displayWidth <= 768;

    if (defaultZero) {
        return {
            marginTop: (isMobile ? props.marginMobileTop : props.marginDesktopTop) ?? '0px',
            marginLeft: (isMobile ? props.marginMobileLeft : props.marginDesktopLeft) ?? '0px',
            marginRight: (isMobile ? props.marginMobileRight : props.marginDesktopRight) ?? '0px',
            marginBottom: (isMobile ? props.marginMobileBottom : props.marginDesktopBottom) ?? '0px',
        };
    }

    return {
        marginTop: isMobile ? props.marginMobileTop : props.marginDesktopTop,
        marginLeft: isMobile ? props.marginMobileLeft : props.marginDesktopLeft,
        marginRight: isMobile ? props.marginMobileRight : props.marginDesktopRight,
        marginBottom: isMobile ? props.marginMobileBottom : props.marginDesktopBottom,
    };
};

/*eslint-disable*/
export const checkIfMobile = (): boolean => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const regexTestFull = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
    const regexTestFirstFour = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
    return regexTestFull.test(userAgent) || regexTestFirstFour.test(userAgent.substring(0, 4));
};

export const checkIfTabletOrMobile = (): boolean => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const regexTestFull = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i;
    const regexTestFirstFour = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
    return regexTestFull.test(userAgent) || regexTestFirstFour.test(userAgent.substring(0, 4));
};

const EMAIL_FORMAT_REGEX = /^(?!.*\.\.)[\w+-]+(?:\.[\w+-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

export const regexValidation = () => {
    return {
        alpha_spaces: /^[a-zA-Z ]*$/,
        username: /.{6,}/,
        amex_test: /^3[47]/,
        alpha_spaces_dashes: /^[a-zA-Z -]*$/,
        customer_name: /^[\x00-\x7F]+$/,
        customer_last_name: /^[\x00-\x7F]{2,}$/,
        approval_code: /^([a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}|\d{3}-\d{4}-\d{3})$/,
        creditone_card: /^(?:\d{4}-\d{4}-\d{4}-\d{4})$/,
        amex_card: /^(?:\d{4}-\d{6}-\d{5,6})$/,
        creditone_card_expiry: /^(0[1-9]|1[0-2])\/?([0-9]{2})$/,
        creditone_cvv: /^(?:\d{3})$/,
        creditone_amex_cvv: /^(?:\d{4})$/,
        creditone_password: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
        initials: /^[a-zA-Z]{2,3}$/,
        day: /^(0[1-9]|[12][0-9]|3[01])$/,
        month: /^(0[1-9]|1[012])$/,
        year: /^(19|20)\d\d$/,
        city: /^[a-zA-Z](( |-|' ?)?[a-zA-Z])*'?$/,
        state: /^[a-zA-Z]{2}$/,
        zip_code: /^[0-9]{5}([0-9]{4})?$/,
        verification_code: /^[0-9]{6,}$/,
        po_box_test:
            /(p\.?\s?o\.?\s?box|p\.?\s?o\.?\s?b\.?|p\.?\s?o\.?|p\.?\s?o\.?\s?bx|post\soffice\s(box)?|p\.?\s?box|p\.?\s?o\.?\s?(drawer|vox])|p\/o\sbox|cluster\sbox|box)(\s?\d+)|general\sdelivery/i,
        last_four_ssn: /^[0-9]{4}$/,
        indexPageName: /^index\w?$/,
        allow: {
            first_name: /^[a-zA-Z]((\. ?|\-| )?[a-zA-Z])*$/,
            last_name: /^([a-zA-Z]((\. ?|' ?|\-| )?[a-zA-Z])*[\.]?){1,}$/,
            middle_initial: /[a-zA-Z]/,
            pa_de_last_name: /^[a-zA-Z]((\. ?|' ?|\-| |,)?[a-zA-Z])*[\.]?$/,
            name_suffix: /([Ss][Rr]|[Jj][Rr])|(X|IX|IV|V?I{0,3})/,
            email: EMAIL_FORMAT_REGEX,
            mailing_address:
                /^[\w\'\-\ \/\,\.\#]*([a-zA-Z][\w\'\-\ \/\,\.\#]*\d|\d[\w\'\-\ \/\,\.\#]*[a-zA-Z])[\w\'\-\ \/\,\.\#]*|([Gg][Ee][Nn][Ee][Rr][Aa][Ll]\s[Dd][Ee][Ll][Ii][Vv][Ee][Rr][Yy])$/,
            home_address:
                /^[\w\'\-\ \/\,\.\#]*([a-zA-Z][\w\'\-\ \/\,\.\#]*\d|\d[\w\'\-\ \/\,\.\#]*[a-zA-Z])[\w\'\-\ \/\,\.\#]*$/,
            ssn: /^\d{9}$/,
            monthlyIncome: /(^\$\d{3,}\.00$)|(\d{3,})/,
            phone: /^\d{10}$/,
        },
        disallow: {
            first_name: /(^[Mm][Rr]\.$)|(^[Mm][Rr][Ss]\.$)|(^[Mm][Ss]\.$)|(^[Mm][Ii][Ss][Ss]$)|(^[Dd][Rr]\.$)/,
            last_name: /(^[Mm][Rr]\.$)|(^[Mm][Rr][Ss]\.$)|(^[Mm][Ss]\.$)|(^[Mm][Ii][Ss][Ss]$)|(^[Dd][Rr]\.$)/,
            middle_initial: /[^a-zA-Z]/,
            pa_de_last_name: /(^[Mm][Rr]\.$)|(^[Mm][Rr][Ss]\.$)|(^[Mm][Ss]\.$)|(^[Mm][Ii][Ss][Ss]$)|(^[Dd][Rr]\.$)/,
            email: /[^\u0020-\u007D]/,
            mailing_address: /[^\u0020-\u007D]/,
            home_address:
                /[^\u0020-\u007D]|.*(PO BOX|P O BOX|P.O. BOX|P. O. BOX|POB|POST OFFICE|P BOX|P O BX|P.O. BX|P. O. BX|P. O BOX|P O DRAWER|P O POX|P O VOX|PO. BOX|P OBOX|P O BX|P.O BOX|P.O.BOX|P.OBOX|P\/O BOX|CLUSTER BOX).*|PO\s*\d{2,}/,
            ssn: /^0{9}$|^1{9}$|^2{9}$|^3{9}$|^4{9}$|^5{9}$|^6{9}$|^7{9}$|^8{9}$|^9{9}$|^9\d{2}(?!7[0-9]|8[0-8]|9[0-2]|9[4-9])|^123456789$/,
            phone: /^800\d{7}$|^888\d{7}$|^877\d{7}$|^866\d{7}$|^855\d{7}$|^0{10}$|^1{10}$|^2{10}$|^3{10}$|^4{10}$|^5{10}$|^6{10}$|^7{10}$|^8{10}$|^9{10}$|^0123456789$|^1234567890$/,
            invalid_address_characters: /~|`|!|@|\$|%|\^|\*|\+|=|{|\[|}|]|\||\\|:|;|"|<|>|\?|\(|\)|\&/gm,
        },
    } as const;
};

export const isValidEmailFormat = (email: string): boolean => {
    return EMAIL_FORMAT_REGEX.test(email);
};

/**
 * Available Groups:
 * href
 * origin
 * protocol
 * host
 * hostName
 * port
 * pathname
 * query
 * hash
 */

/*eslint-enable*/

export const sanitizeHtml = function (html: string): string {
    const ignore = ['(sup)', '(sub)'];
    const regex = new RegExp('</?\\b(?!' + ignore.join('|') + ').+?/?>', 'g');
    return html.replace(regex, '');
};

export const getSelectedProgramCodeIndex = function (preQualResponse: TPrequalResponse, selectedOfferProgramCode: string): number {
    if (!preQualResponse || !preQualResponse.ApplicationInformation || !preQualResponse.ApplicationInformation.length) {
        console.error('PQ Response Application Info is invalid');
        return -1;
    }

    const applicationInfo = preQualResponse.ApplicationInformation;

    if (!selectedOfferProgramCode || applicationInfo.length === 1) {
        return 0;
    }

    for (let i = 0; i < applicationInfo.length; i++) {
        if (
            applicationInfo[i].ProgramRates &&
            applicationInfo[i].ProgramRates.ProgramCode === selectedOfferProgramCode
        ) {
            return i;
        }
    }

    return -1;
};

export const calculateAge = function (dob: string): number {
    if (!dob) {
        return -1;
    }
    const currentDate = new Date();
    const splicedBirthDate = dob.split('/');
    const birthDate = new Date(parseInt(splicedBirthDate[2]), parseInt(splicedBirthDate[0]) - 1, parseInt(splicedBirthDate[1]));
    let age = currentDate.getFullYear() - birthDate.getFullYear();
    const month = currentDate.getMonth() - birthDate.getMonth();
    if (month < 0 || (month === 0 && currentDate.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

export const convertToMoneyString = (input: number | string | undefined): string => {
    if (input === undefined || input === null) {
        return null;
    } else if (typeof input === 'string') {
        input = Number(input);
    }
    if (Number.isNaN(input)) {
        return null;
    }
    return input.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const checkExistsDiacriticSpecialCharacters = (enteredValue: string): boolean => {
    if (!enteredValue || (enteredValue && enteredValue.length < 1)) {
        return false;
    }
    const accents = '°ÀÁÂÃÄÅàáâãäåÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž?¿';
    const extraSpecialChars = '°?¿';
    let invalidCharsFound: boolean = false;
    const splitted = enteredValue.split('');

    for (let i = 0; i < splitted.length; i++) {
        if (accents.indexOf(splitted[i]) !== -1) {
            invalidCharsFound = true;
            break;
        }
        if (extraSpecialChars.indexOf(splitted[i]) !== -1) {
            invalidCharsFound = true;
            break;
        }
    }
    return invalidCharsFound;
};

export const escapeAnchorLinks = (element: string): string => {
    const regex = /<a[\s\S]*?<\/a>/gi;
    const tagIndexes = [];
    let match;
    while ((match = regex.exec(element)) !== null) {
        tagIndexes.push({
            startIndex: match.index,
            endIndex: regex.lastIndex,
        });
    }

    tagIndexes.forEach(({ startIndex, endIndex }) => {
        const anchorTag = element.substring(startIndex, endIndex);
        element = element.replace(
            anchorTag,
            anchorTag.replace(/"/g, '\\"'),
        );
    });

    return element;
};

export class DateUtils {
    present: Date;
    future: Date;

    constructor(present: Date, future: Date) {
        this.present = present;
        this.future = future;
    }

    private static readonly formulae = {
        base: (present: Date, future: Date, perUnit: number) => {
            // Difference in milliseconds
            const diff = future.getTime() - present.getTime();
            return diff / perUnit;
        },
        days: (present: Date, future: Date) => DateUtils.formulae.base(present, future, (1000 * 60 * 60 * 24)),
        hours: (present: Date, future: Date) => DateUtils.formulae.base(present, future, (1000 * 60 * 60)),
        minutes: (present: Date, future: Date) => DateUtils.formulae.base(present, future, (1000 * 60)),
        seconds: (present: Date, future: Date) => DateUtils.formulae.base(present, future, (1000)),
    };

    public inDays() {
        return DateUtils.formulae.days(this.present, this.future);
    }
    public inHours() {
        return DateUtils.formulae.hours(this.present, this.future);
    }
    public inMinutes() {
        return DateUtils.formulae.minutes(this.present, this.future);
    }
    public inSeconds() {
        return DateUtils.formulae.seconds(this.present, this.future);
    }

    public static inDays(present: Date, future: Date) {
        return DateUtils.formulae.days(present, future);
    }
    public static inHours(present: Date, future: Date) {
        return DateUtils.formulae.hours(present, future);
    }
    public static inMinutes(present: Date, future: Date) {
        return DateUtils.formulae.minutes(present, future);
    }
    public static inSeconds(present: Date, future: Date) {
        return DateUtils.formulae.seconds(present, future);
    }

    public isDayAfter() {
        return DateUtils.isDayAfter(this.present, this.future);
    }

    public static isDayAfter(present: Date, future: Date) {
        if ((future.getTime() - present.getTime()) > 0) {
            return new Date(present.getTime() + (1000 * 60 * 60 * 24)).getDay() === future.getDay();
        }
        return false;
    }

    public static hasExpired(toEvaluate: Date | String) {
        if (toEvaluate === null || toEvaluate === undefined) {
            return true;
        }
        if (typeof toEvaluate === 'string') {
            toEvaluate = new Date(toEvaluate);
            if (toEvaluate.toString() === 'Invalid Date') {
                return true;
            }
        }
        const now = new Date();
        return toEvaluate < now;
    }

    public static getTimeDelta(timeDelta: number = 100000) {
        return new Date(new Date().getTime() + timeDelta);
    }
}

export const updateInputMask = (element: HTMLInputElement, value: string): void => {
    element.value = value;
    element.selectionStart = element.selectionEnd = value.length;
    return TestUtils.Simulate.change(element);
};

/**
 * Checks the User Agent to see if the user is running chrome (webkit engine)on iOS
**/
export const isUserOnChromeIOS = (): boolean => {
    return navigator.userAgent.indexOf('CriOS') >= 0; // iOS&Chrome?
};

/**
 * Workaround to print a model if the user is running chrome (webkit engine)on iOS
 * Uses a workaround by creating a new tab that renders the content.
 * Then it dismisses the tab immediatel after the print dialog is open taking
 * the user back to the previous page
 * 
 * @param styles The sass style to render the modal to be printed
 * @param contentToPrint: useRef<HTMLDivElement>(null);
 */

export const printOnIOSChrome = (styles: string, contentToPrint: HTMLElement, shouldCenter: Boolean | undefined = true): void => {
    //workaround for chrome on iOS
    //This will need to be adjusted when chrome gets its own native engine on iOS as opposed to webkit
    const modalToPrint: Window = window.open('', '');
    modalToPrint.document.open();
    const style = modalToPrint.document.createElement('style');
    if (shouldCenter) {
        modalToPrint.document.write(
            '</head><body onLoad="self.print()"><center>',
        );
    } else {
        modalToPrint.document.write(
            '</head><body onLoad="self.print()">',
        );
    }
    modalToPrint.document.write(contentToPrint.innerHTML);
    style.innerHTML = styles;
    modalToPrint.document.head.appendChild(style);
    if (shouldCenter) {
        modalToPrint.document.write('</center></body></html>');
    } else {
        modalToPrint.document.write('</body></html>');
    }
    modalToPrint.addEventListener('afterprint', () => {
        modalToPrint.close();
    });
    modalToPrint.document.close();

};

export const isIOS = (): boolean => {
    return /iPhone|iPad/i.test(navigator.userAgent);
};

export const wrapTrademarkSuperscript = (input: string): string => {
    if (!input) return;
    if (input.includes('<sup>®</sup>')) {
        return input;
    } else if (input.includes('<sup>Ⓡ</sup>')) {
        return input;
    }
    return input.replaceAll(/[®Ⓡ]/g, (match: string) => {
        return `<sup>${match}</sup>`;
    });
};

/**
 * Takes an array of selectors and equalizes the height of all the elements
 * retrieved from each selector. The resulting height will reflect the element
 * with the largest height value.
 * 
 * @param selectors String[]
 */
export function equalizeHeights(selectors: string[]): void {
    selectors.forEach((selector) => {
        const elems = Array.from(document.querySelectorAll(selector));

        if (!elems.length) {
            return;
        }

        const heights = elems.map((el: any) => {
            el.style.height = ''; // reset heights to get the true height
            return el.offsetHeight;
        });
        const maxHeight = Math.max(...heights, 0);

        elems.forEach((el: any) => {
            el.style.height = maxHeight ? maxHeight + 'px' : '';
        });
    });
}

export const findMobileTooltipPosition = (index: number, rowLength: 2 | 3 = 3): 'top-left' | 'top-center' | 'top-right' | 'bottom-center' => {
    const position = index + 1;
    if (position % rowLength === 1) { //if position is on the left notch should be on the left
        return 'top-left';
    } else if (position % rowLength === 2) { //if position is in the middle notch should be in the center
        return 'top-center';
    } else if (position % rowLength === 0) { //if position is on the right notch should be on the right
        return 'top-right';
    }

    return 'bottom-center';

};

/**
 * Checks if video media devices are disabled.
 * @param callback - takes a boolean that indicates if disabled or not.
 */
export async function checkVideoMediaDevicesDisabled(callback: Function): Promise<void> {
    if (navigator?.mediaDevices) {
        await navigator.mediaDevices.getUserMedia({ video: true })
            .then(async () => {
                callback(await isVideoMediaDisabled());
            })
            .catch(() => {
                callback(true);
            });
    } else {
        callback(true);
    }
}

/**
 * Checks the users enumerated video devices to see if they are disabled.
 */
export async function isVideoMediaDisabled(): Promise<boolean> {
    const devices = await navigator.mediaDevices.enumerateDevices()
        .then((devices) => devices.filter((device: MediaDeviceInfo) => device.kind === 'videoinput'));

    if (!devices.length) {
        return true;
    }

    return devices.some((device) => device?.deviceId === '');
}

export function isAuthorMode(): boolean {
    return (window.location.host.indexOf('4502') > -1) || (window.location.host.indexOf('3000') > -1);
}

/**
 * Safely tries to JSON.parse() the specified value.
 * @param toParse The value to try and parse.
 * @param defaultValue The default return value if there is an error.
 * @returns The result of JSON.parse() or defaultValue.
 */
export function safeParse(toParse: any, defaultValue: string | Record<string, unknown> = ''): Record<string, unknown> {
    try {
        return JSON.parse(toParse);
    } catch {
        // TODO: Correct this blind typing. safeParse is used in corporate and TermRates.
        return defaultValue as Record<string, unknown>;
    }
}

export function isBase64(str: string): boolean {
    try {
        return btoa(atob(str)) === str;
    } catch (e) {
        return false;
    }
}

export const getApiTokens = (): Record<string, unknown> => {
    const tkProperty = PathUtils.getMetaPropertyValue('aton');
    let aton = '';
    if (isBase64(tkProperty)) {
        aton = atob(tkProperty);
    } else {
        aton = tkProperty;
    }
    const apiTokens = JSON.parse(aton || '{}') as Record<string, unknown>;

    return apiTokens;
};
export const getApiToken = (key: string): unknown => {

    const apiTokens = getApiTokens();
    const token = apiTokens[key] ?? null;

    if (!token) {
        // eslint-disable-next-line no-console
        console.log(`Cannot retrieve ${key} Api Token`);
    }

    return token;
};

export const shortenText = (text: string): string => {
    const characterLimit = 150;
    if (text && text.length > characterLimit) {
        let words = text.split(' ');
        let tally = 0;
        let wordCount = 0;

        for (const word of words) {
            if (tally + word.length > characterLimit) {
                words = words.slice(0, wordCount);
                break;
            } else {
                wordCount += 1;
                tally += word.length;
            }
        }
        const result = words.join(' ');
        return result;
    }
    // Preview text is short enough to remain untruncated.
    return text;
};

export const retrieveDBOsgiUrl = (currentLink: string): string => {
    const osgiBaseUrl = safeParse(PathUtils.getMetaPropertyValue('digitalBankTerms'))?.osgiBaseUrl;
    const useNewUrlsFlag = PathUtils.getMetaPropertyValue('useNewUrls') === 'true'
    if (osgiBaseUrl) {
        const regex = /^(?:https?:\/\/[^/]+)(.*?)[?#]?$/;
        const pathName = currentLink ? (currentLink.match(regex) ? currentLink.match(regex)[1] : currentLink) : '';
        if (useNewUrlsFlag) {
        const queryString = currentLink?.includes('?')
                    ? currentLink.substring(currentLink.indexOf('?'))
                    : '';

            return osgiBaseUrl + queryString;
        }
        return osgiBaseUrl + pathName;
    }
    return currentLink;
};

export const UUIDv4 = function b(a?: number): string {
    return a
        ? (a ^ ((crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296 * 16) >> (a / 4))).toString(16)
        // @ts-ignore
        : ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, b);
};

export const normalizeCardData = (card: TCreditCard, useCoreProducts: boolean) => {
    if(!card) {
        return;
    }
    if(useCoreProducts) {
        return {
            creditStatus: card.coreProductDetails?.creditLevels,
            cardBullets: card.coreProductDetails?.browseCardsBullets,
            cardBenefit: card.coreProductDetails?.browseCardsFilterValue,
            staticLink: card.coreProductDetails?.termsStaticLink,   
            dynamicLink: card.coreProductDetails?.dynamicTermsLink,
            productName: card.coreProductDetails?.productTitle,
            bazaarVoiceId: card.coreProductDetails?.reviewsId,
            benefitsBarIcon: card.coreProductDetails?.browseCardsBenefitBarIcon,
            benefitsBarTitle: card.coreProductDetails?.browseCardsBenefitBarTitle,
            fee: card.coreProductDetails?.annualFee,
            imagePath: card.coreProductDetails?.primaryImage,
            imageAltText: card.coreProductDetails?.primaryImageAltText,
            reviewsLink: `${card.coreProductDetails?.productPage}/reviews`,
            reviewLinkTarget: '_self',
            productDescription: card.coreProductDetails?.productTagline,
            annualFeeLongText: card.coreProductDetails?.annualFeeLongText,
            annualFeeText: card.coreProductDetails?.annualFeeText,
            preQualifyLink: `/pre-qualification/data-entry/index?C1BSourceID=${card.coreProductDetails?.sourceId}${card.coreProductDetails?.browseCardsPQParameters ?? ''}`,
            productPageLink: card.coreProductDetails?.productPage,
            productFamilyLink: card.coreProductDetails?.browseCardsProductFamilyLink,
            productFamilyText: card.coreProductDetails?.browseCardsProductFamilyLinkText,
            currentMemberText: card.coreProductDetails?.browseCardsCurrentMemberText
        }
    }
    return {
        creditStatus: card.creditStatus,
        cardBullets: card.descriptionBullets,
        cardBenefit:  card.creditBenefit,
        staticLink: card?.staticLink,
        dynamicLink: card?.dynamicTermsLink,
        productName: card.productName,
        bazaarVoiceId: card?.bazaarvoiceID,
        benefitsBarIcon: card.benefitBarImg,
        benefitsBarTitle: card?.benefitBarTitle,
        fee: card.fee,
        imagePath: card.imagePath,
        imageAltText: card.cardImageAltText,
        reviewsLink: card.bazaarvoiceStarsLink,
        reviewLinkTarget: card.bazaarvoiceStarsLinkTarget,
        productDescription: card.productDescription,
        annualFeeLongText: card.promotionText,
        annualFeeText: card.supplementalPromotionText,
        preQualifyLink: card.preQualifyLink,
        productPageLink: card.cardDetailsLink,
        productFamilyLink: card.familyPath,
        productFamilyText: card.familyPathText,
        currentMemberText: card.memberText
    }
}
